import { motion, useIsPresent } from "motion/react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { formatConditionBadge } from "../conditionBadge";
import { isConditionComplete, isLegacyCondition } from "../conditions";
import { SEGMENT_TRANSITION } from "../constants";
import type { FormCondition } from "../schemas";
import { ConditionBadge } from "./ConditionBadge";
import { LegacyConditionChip } from "./LegacyConditionChip";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/v2/popover";
import type {
  ConditionOverlap,
  SegmentField,
} from "@/features/segments/types";
import { cn } from "@/lib/utils/cn";

type ConditionListItemProps = {
  condition: FormCondition;
  fields: SegmentField[];
  hasError: boolean;
  isOpen: boolean;
  readOnly: boolean;
  overlap?: ConditionOverlap;
  logicLabel?: string;
  onOpenChange: (open: boolean) => void;
  onOpenAutoFocus: (event: Event) => void;
  onRemove: () => void;
  children: ReactNode;
};

export function ConditionListItem({
  condition,
  fields,
  hasError,
  isOpen,
  readOnly,
  overlap,
  logicLabel,
  onOpenChange,
  onOpenAutoFocus,
  onRemove,
  children,
}: ConditionListItemProps) {
  const { t } = useTranslation();
  const isPresent = useIsPresent();
  const parts = formatConditionBadge(condition, fields, t);

  return (
    <motion.li
      layout="position"
      initial={false}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={SEGMENT_TRANSITION}
      aria-hidden={!isPresent || undefined}
      className={cn(
        "inline-flex max-w-full items-center gap-2",
        !isPresent && "pointer-events-none",
      )}
    >
      {parts && isLegacyCondition(condition, fields) ? (
        <LegacyConditionChip
          conditionKey={condition.clientKey}
          parts={parts}
          readOnly={readOnly || !isPresent}
          onRemove={onRemove}
        />
      ) : (
        <Popover open={isPresent && isOpen} onOpenChange={onOpenChange}>
          <PopoverTrigger asChild>
            <ConditionBadge
              data-condition-key={isPresent ? condition.clientKey : undefined}
              tabIndex={isPresent ? undefined : -1}
              parts={parts}
              isInverted={condition.isInverted}
              isIncomplete={!isConditionComplete(condition)}
              hasError={hasError}
              overlap={overlap}
            />
          </PopoverTrigger>
          <PopoverContent
            align="start"
            sideOffset={2}
            collisionPadding={16}
            onOpenAutoFocus={onOpenAutoFocus}
            onCloseAutoFocus={(event) => {
              if (!isPresent) event.preventDefault();
            }}
            className="w-[min(44rem,calc(100vw-2rem))] bg-card"
          >
            {isPresent && children}
          </PopoverContent>
        </Popover>
      )}
      {logicLabel && (
        <span
          aria-hidden
          className="font-mono text-sm font-semibold tracking-wide text-muted-foreground"
        >
          {logicLabel}
        </span>
      )}
    </motion.li>
  );
}
